import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faCheckCircle, faCoins, faChartLine, faBuilding, faUser,
    faArrowRight, faSyncAlt,
    faAddressBook, faTimesCircle, faTrophy, faCalendarAlt, faUsers,
    faShieldAlt, faExclamationTriangle, faSkullCrossbones, faUserCheck, faTimes,
    faEllipsisH, faBullseye
} from '@fortawesome/free-solid-svg-icons';

const STAGES = [
    { key: "Qualification", label: "Qualification", color: "#1677ff", bg: "#eff6ff" },
    { key: "Approached", label: "Approached", color: "#52525b", bg: "#f4f4f5" },
    { key: "Follow-up", label: "Follow-up", color: "#71717a", bg: "#f4f4f5" },
    { key: "Meeting Scheduled", label: "Meeting Scheduled", color: "#a1a1aa", bg: "#f4f4f5" },
    { key: "Proposal Preparation", label: "Proposal Prep", color: "#d97706", bg: "#fef3c7" },
    { key: "Review/Discussion", label: "Review / Discussion", color: "#3f3f46", bg: "#f4f4f5" },
    { key: "Completed", label: "Completed", color: "#16a34a", bg: "#f0fdf4" }
];

const CHART_COLORS = ['#1677ff', '#52525b', '#71717a', '#a1a1aa', '#16a34a', '#d97706'];

// Smooth cubic Bezier spline area & line path generator for SVG Area Curves
const getSvgPath = (points, isArea = false, bottomY = 175) => {
    if (!points || points.length === 0) return '';
    if (points.length === 1) {
        if (isArea) {
            return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)} L ${points[0].x.toFixed(1)} ${bottomY} Z`;
        }
        return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
    }

    const d = [`M ${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`];

    for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(0, i - 1)];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[Math.min(points.length - 1, i + 2)];

        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;

        d.push(`C ${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`);
    }

    if (isArea) {
        const last = points[points.length - 1];
        const first = points[0];
        d.push(`L ${last.x.toFixed(1)},${bottomY} L ${first.x.toFixed(1)},${bottomY} Z`);
    }

    return d.join(' ');
};

// Subcomponent for Area Curve Trend Chart with Rich Interactive Hover Tooltip
function AreaCurveTrendChart({ trendData, chartId, metricLabel, metricVerb, emptyMessage, emptyIcon }) {
    const [hoverState, setHoverState] = useState(null);

    if (!trendData || trendData.totalAll === 0) {
        return (
            <div className="trend-empty-state">
                <FontAwesomeIcon icon={emptyIcon} className="trend-empty-icon" />
                <p className="trend-empty-text">{emptyMessage}</p>
            </div>
        );
    }

    const { monthData, activeReps, maxTotal } = trendData;
    const N = monthData.length;
    const svgWidth = 600;
    const svgHeight = 210;
    const paddingLeft = 38;
    const paddingRight = 24;
    const paddingTop = 18;
    const bottomY = 168;
    const chartWidth = svgWidth - paddingLeft - paddingRight;
    const chartHeight = bottomY - paddingTop;

    const effectiveMax = Math.max(maxTotal, 1);
    const yTicks = [
        0,
        Math.ceil(effectiveMax / 2),
        effectiveMax
    ];

    const getX = (i) => {
        if (N <= 1) return paddingLeft + chartWidth / 2;
        return paddingLeft + (i / (N - 1)) * chartWidth;
    };

    const getY = (val) => {
        return bottomY - (val / effectiveMax) * chartHeight;
    };

    const totalPoints = monthData.map((m, i) => ({
        x: getX(i),
        y: getY(m.total),
        total: m.total,
        label: m.label,
        repCounts: m.repCounts
    }));

    const repSeries = activeReps.map(rep => {
        const points = monthData.map((m, i) => ({
            x: getX(i),
            y: getY(m.repCounts[rep.empId] || 0),
            val: m.repCounts[rep.empId] || 0,
            label: m.label
        }));
        return {
            ...rep,
            points,
            areaPath: getSvgPath(points, true, bottomY),
            linePath: getSvgPath(points, false, bottomY)
        };
    });

    const totalAreaPath = getSvgPath(totalPoints, true, bottomY);
    const totalLinePath = getSvgPath(totalPoints, false, bottomY);

    return (
        <div
            className="area-curve-chart-container"
            onMouseLeave={() => setHoverState(null)}
        >
            <svg
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                className="area-curve-svg area-curve-svg-responsive"
            >
                <defs>
                    {activeReps.map((rep) => (
                        <linearGradient key={`grad-${chartId}-${rep.empId}`} id={`grad-${chartId}-${rep.empId}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={rep.color} stopOpacity="0.32" />
                            <stop offset="100%" stopColor={rep.color} stopOpacity="0.01" />
                        </linearGradient>
                    ))}
                    <linearGradient id={`grad-total-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#38b6ff" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="#38b6ff" stopOpacity="0.00" />
                    </linearGradient>
                </defs>

                {/* Horizontal Gridlines & Y-Axis labels */}
                {yTicks.map((tick, idx) => {
                    const y = getY(tick);
                    return (
                        <g key={`ytick-${idx}`}>
                            <line
                                x1={paddingLeft}
                                y1={y}
                                x2={svgWidth - paddingRight}
                                y2={y}
                                stroke="#e2e8f0"
                                strokeDasharray={tick === 0 ? 'none' : '4 4'}
                                strokeWidth={tick === 0 ? '1.5' : '1'}
                            />
                            <text
                                x={paddingLeft - 8}
                                y={y + 4}
                                textAnchor="end"
                                fontSize="10"
                                fontWeight="600"
                                fill="#94a3b8"
                            >
                                {tick}
                            </text>
                        </g>
                    );
                })}

                {/* Total Area Backdrop */}
                {activeReps.length > 1 && (
                    <>
                        <path d={totalAreaPath} fill={`url(#grad-total-${chartId})`} />
                        <path d={totalLinePath} fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="3 3" />
                    </>
                )}

                {/* Individual Rep Curves */}
                {repSeries.map((s) => (
                    <g key={`series-${s.empId}`}>
                        <path d={s.areaPath} fill={`url(#grad-${chartId}-${s.empId})`} />
                        <path
                            d={s.linePath}
                            fill="none"
                            stroke={s.color}
                            strokeWidth={hoverState?.focusedRepId === s.empId ? "3.8" : "2.8"}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="area-curve-line"
                        />
                        {/* Points on the curve */}
                        {s.points.map((pt, pIdx) => {
                            const isHoveredCol = hoverState?.monthIndex === pIdx;
                            const isFocusedDot = isHoveredCol && hoverState?.focusedRepId === s.empId;
                            return (
                                <circle
                                    key={`pt-${s.empId}-${pIdx}`}
                                    cx={pt.x}
                                    cy={pt.y}
                                    r={isFocusedDot ? "7" : (pt.val > 0 ? (isHoveredCol ? "5.5" : "4.5") : "2")}
                                    fill={pt.val > 0 ? "#ffffff" : s.color}
                                    stroke={s.color}
                                    strokeWidth={isFocusedDot ? "3.5" : (pt.val > 0 ? "2.5" : "1")}
                                    className="curve-point-dot"
                                    onMouseEnter={(e) => {
                                        e.stopPropagation();
                                        setHoverState({
                                            monthIndex: pIdx,
                                            monthLabel: pt.label,
                                            total: monthData[pIdx].total,
                                            repCounts: monthData[pIdx].repCounts,
                                            x: pt.x,
                                            y: pt.y,
                                            focusedRepId: s.empId,
                                            focusedRepName: s.fullName,
                                            focusedRepVal: pt.val
                                        });
                                    }}
                                />
                            );
                        })}
                    </g>
                ))}

                {/* Vertical Guideline when Hovering */}
                {hoverState && (
                    <line
                        x1={hoverState.x}
                        y1={paddingTop - 6}
                        x2={hoverState.x}
                        y2={bottomY}
                        stroke="#38b6ff"
                        strokeWidth="1.5"
                        strokeDasharray="3 3"
                        opacity="0.85"
                    />
                )}

                {/* Invisible Hover Catchers for each Month column */}
                {monthData.map((m, i) => {
                    const colWidth = N > 1 ? chartWidth / (N - 1) : chartWidth;
                    const colX = N > 1 ? Math.max(0, getX(i) - colWidth / 2) : paddingLeft;
                    return (
                        <rect
                            key={`hover-col-${i}`}
                            x={colX}
                            y={0}
                            width={colWidth}
                            height={bottomY + 40}
                            fill="transparent"
                            className="cursor-pointer"
                            onMouseEnter={() => {
                                setHoverState({
                                    monthIndex: i,
                                    monthLabel: m.label,
                                    total: m.total,
                                    repCounts: m.repCounts,
                                    x: getX(i),
                                    y: getY(m.total),
                                    focusedRepId: null
                                });
                            }}
                        />
                    );
                })}

                {/* X-Axis Month Labels & Total Counts */}
                {totalPoints.map((pt, i) => (
                    <g key={`month-lbl-${i}`}>
                        <text
                            x={pt.x}
                            y={bottomY + 20}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight={hoverState?.monthIndex === i ? "800" : "600"}
                            fill={hoverState?.monthIndex === i ? "#0284c7" : "#64748b"}
                        >
                            {pt.label}
                        </text>
                        {pt.total > 0 && (
                            <text
                                x={pt.x}
                                y={Math.min(pt.y - 8, bottomY - 14)}
                                textAnchor="middle"
                                fontSize="10.5"
                                fontWeight="700"
                                fill="#334155"
                            >
                                {pt.total}
                            </text>
                        )}
                    </g>
                ))}
            </svg>

            {/* Floating Rich Tooltip with Adaptive Smart Positioning */}
            {hoverState && (() => {
                const xRatio = hoverState.x / svgWidth;
                const yRatio = hoverState.y / svgHeight;
                // If point is in the upper 48% of chart, position tooltip below it so it never gets clipped at the top
                const isTopHalf = yRatio < 0.48;
                const yTransform = isTopHalf ? '14px' : 'calc(-100% - 14px)';

                let xTransform = '-50%';
                let ttLeft = `${(hoverState.x / svgWidth) * 100}%`;
                if (xRatio < 0.22) {
                    xTransform = '0%';
                    ttLeft = `${Math.max(2, (hoverState.x / svgWidth) * 100)}%`;
                } else if (xRatio > 0.78) {
                    xTransform = '-100%';
                    ttLeft = `${Math.min(98, (hoverState.x / svgWidth) * 100)}%`;
                }

                return (
                    <div
                        className="trend-floating-tooltip"
                        style={{
                            left: ttLeft,
                            top: `${(hoverState.y / svgHeight) * 100}%`,
                            transform: `translate(${xTransform}, ${yTransform})`
                        }}
                    >
                        <div className="trend-tt-header">
                            <span className="trend-tt-month">{hoverState.monthLabel}</span>
                            <span className="trend-tt-total-badge">
                                {hoverState.total} Total {metricVerb || metricLabel}
                            </span>
                        </div>
                        <div className="trend-tt-divider" />
                        <div className="trend-tt-body">
                            {activeReps
                                .filter(r => (hoverState.repCounts[r.empId] || 0) > 0 || r.empId === hoverState.focusedRepId)
                                .sort((a, b) => (hoverState.repCounts[b.empId] || 0) - (hoverState.repCounts[a.empId] || 0))
                                .map(rep => {
                                    const count = hoverState.repCounts[rep.empId] || 0;
                                    const isFocused = rep.empId === hoverState.focusedRepId;
                                    return (
                                        <div
                                            key={rep.empId}
                                            className={`trend-tt-row ${isFocused ? 'trend-tt-row-focused' : ''}`}
                                        >
                                            <div className="trend-tt-rep-info">
                                                <span className="trend-tt-dot" style={{ background: rep.color }}></span>
                                                <span className="trend-tt-name" style={{ fontWeight: isFocused ? 700 : 500 }}>
                                                    {rep.fullName}
                                                </span>
                                            </div>
                                            <span className="trend-tt-count" style={{ color: rep.color }}>
                                                <strong>{count}</strong> {count === 1 ? (metricVerb?.slice(0, -1) || 'lead') : (metricVerb || 'leads')}
                                            </span>
                                        </div>
                                    );
                                })}
                            {activeReps.every(r => (hoverState.repCounts[r.empId] || 0) === 0) && (
                                <div className="trend-tt-empty">No activity recorded</div>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* Rep Legend */}
            <div className="trend-legend-wrap">
                {activeReps.map((rep) => (
                    <div
                        key={rep.empId}
                        className="trend-legend-pill"
                        style={{
                            opacity: hoverState?.focusedRepId && hoverState.focusedRepId !== rep.empId ? 0.45 : 1,
                            borderColor: hoverState?.focusedRepId === rep.empId ? rep.color : undefined
                        }}
                    >
                        <span className="trend-legend-dot" style={{ background: rep.color }}></span>
                        <span className="trend-legend-name">{rep.name}</span>
                        <span className="trend-legend-count">({rep.total})</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Dashboard() {
    const navigate = useNavigate();
    const { roleId, empId, isSuper, isManager, getRbacParams, activeOrgId } = useAuth();
    const [opportunities, setOpportunities] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [targets, setTargets] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showQuickOppModal, setShowQuickOppModal] = useState(false);
    const [showQuickAccModal, setShowQuickAccModal] = useState(false);
    const [accModalType, setAccModalType] = useState('Organization');
    const [showNewDropdown, setShowNewDropdown] = useState(false);
    const [oppForm, setOppForm] = useState({ OpprName: '', Account: '', Amount: '', Stage: 'Qualification', Status: 'Open' });
    const [accForm, setAccForm] = useState({ AccountType: 'Organization', AccountName: '', ContactPerson: '', ContactDesignation: '', CntPhone1: '', CntMail1: '', Linkedin: '', City: 'Lahore', Status: 'Lead', Priority: 'Medium', Notes: '' });

    const fetchAllDashboardData = async () => {
        try {
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();
            const [oppsRes, accsRes, targetsRes, usersRes] = await Promise.all([
                api.get(`/opportunities?${qstr}`).catch(() => ({ data: [] })),
                api.get(`/leads?${qstr}`).catch(() => ({ data: [] })),
                api.get(`/targets?${qstr}`).catch(() => ({ data: [] })),
                api.get(`/users?${qstr}`).catch(() => ({ data: [] }))
            ]);
            setOpportunities(oppsRes.data || []);
            setAccounts(accsRes.data || []);
            setTargets(targetsRes.data || []);
            setUsers(usersRes.data || []);
        } catch (err) {
            console.error("Dashboard fetch error:", err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { fetchAllDashboardData(); }, [roleId, empId, activeOrgId]);
    const handleManualRefresh = () => { setRefreshing(true); fetchAllDashboardData(); };

    const setPresetDates = (type) => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const fyStartYear = month >= 6 ? year : year - 1;
        const fyEndYear = fyStartYear + 1;

        if (type === 'ALL') {
            setStartDate('');
            setEndDate('');
        } else if (type === 'CURRENT_H1') {
            setStartDate(`${fyStartYear}-07-01`);
            setEndDate(`${fyStartYear}-12-31`);
        } else if (type === 'CURRENT_H2') {
            setStartDate(`${fyEndYear}-01-01`);
            setEndDate(`${fyEndYear}-06-30`);
        } else if (type === 'FULL_FY') {
            setStartDate(`${fyStartYear}-07-01`);
            setEndDate(`${fyEndYear}-06-30`);
        }
    };

    const filteredOpportunities = useMemo(() => {
        if (!startDate && !endDate) return opportunities;
        return opportunities.filter(opp => {
            if (!opp.CreatedAt) return true;
            const created = opp.CreatedAt.slice(0, 10);
            if (startDate && created < startDate) return false;
            if (endDate && created > endDate) return false;
            return true;
        });
    }, [opportunities, startDate, endDate]);

    const filteredAccounts = useMemo(() => {
        if (!startDate && !endDate) return accounts;
        return accounts.filter(acc => {
            if (!acc.CreatedAt) return true;
            const created = acc.CreatedAt.slice(0, 10);
            if (startDate && created < startDate) return false;
            if (endDate && created > endDate) return false;
            return true;
        });
    }, [accounts, startDate, endDate]);

    const filteredTargets = useMemo(() => {
        if (!startDate && !endDate) return targets;
        return targets.filter(t => {
            const s = t.StartDate ? t.StartDate.slice(0, 10) : '';
            const e = t.EndDate ? t.EndDate.slice(0, 10) : '';
            if (startDate && e && e < startDate) return false;
            if (endDate && s && s > endDate) return false;
            if (t.Period) {
                if (startDate.includes('-07-01') && endDate.includes('-12-31') && t.Period.includes('H2')) return false;
                if (startDate.includes('-01-01') && endDate.includes('-06-30') && t.Period.includes('H1')) return false;
            }
            return true;
        });
    }, [targets, startDate, endDate]);

    const periodLabel = useMemo(() => {
        if (!startDate && !endDate) return 'Total';
        if (startDate.includes('-07-01') && endDate.includes('-12-31')) return 'H1';
        if (startDate.includes('-01-01') && endDate.includes('-06-30')) return 'H2';
        if (startDate.includes('-07-01') && endDate.includes('-06-30')) return 'Full FY';
        return 'Period';
    }, [startDate, endDate]);

    // Multi-index mapping for fast opportunity lookups (by Account ID and by Account Name)
    const oppsByAccount = useMemo(() => {
        const map = new Map();
        opportunities.forEach(opp => {
            if (opp.Account) {
                const accId = Number(opp.Account);
                if (!map.has(accId)) map.set(accId, []);
                map.get(accId).push(opp);
            }
            if (opp.AccountName) {
                const accNameKey = String(opp.AccountName).trim().toLowerCase();
                if (!map.has(accNameKey)) map.set(accNameKey, []);
                map.get(accNameKey).push(opp);
            }
        });
        return map;
    }, [opportunities]);

    const getLeadOpps = (acc) => {
        if (!acc) return [];
        const byId = acc.AccountID ? oppsByAccount.get(Number(acc.AccountID)) : null;
        if (byId && byId.length > 0) return byId;
        const byName = acc.AccountName ? oppsByAccount.get(String(acc.AccountName).trim().toLowerCase()) : null;
        return byName || [];
    };

    const getLeadEffectiveStatus = (acc) => {
        if (!acc) return 'New Lead';
        const opps = getLeadOpps(acc);
        if (opps && opps.length > 0) return 'Opportunity Created';
        return acc.ApproachStatus || 'New Lead';
    };

    const metrics = useMemo(() => {
        const totalPipeline = filteredOpportunities.reduce((sum, opp) => sum + (Number(opp.Amount) || 0), 0);
        const openDeals = filteredOpportunities.filter(o => o.Status === "Open" && o.Stage !== "Completed");
        const openTotal = openDeals.reduce((sum, opp) => sum + (Number(opp.Amount) || 0), 0);
        const wonDeals = filteredOpportunities.filter(o => o.Status === "Win" || o.Stage === "Completed");
        const wonTotal = wonDeals.reduce((sum, opp) => sum + (Number(opp.Amount) || 0), 0);
        const lostDeals = filteredOpportunities.filter(o => o.Status === "Lose");
        const lostTotal = lostDeals.reduce((sum, opp) => sum + (Number(opp.Amount) || 0), 0);
        const closedDealsCount = wonDeals.length + lostDeals.length;
        const winRatio = closedDealsCount > 0 ? Math.round((wonDeals.length / closedDealsCount) * 100) : (filteredOpportunities.length > 0 ? Math.round((wonDeals.length / filteredOpportunities.length) * 100) : 0);
        const lostRatio = closedDealsCount > 0 ? Math.round((lostDeals.length / closedDealsCount) * 100) : (filteredOpportunities.length > 0 ? Math.round((lostDeals.length / filteredOpportunities.length) * 100) : 0);

        const leadAccounts = filteredAccounts.filter(a => a.Status === "Lead" || (!a.Status));
        const customerAccounts = filteredAccounts.filter(a => a.Status === "Customer");
        const deadAccounts = filteredAccounts.filter(a => a.Status === "Dead");
        const highPriority = filteredAccounts.filter(a => (a.Priority || '').toLowerCase() === 'high');
        const mediumPriority = filteredAccounts.filter(a => (a.Priority || '').toLowerCase() === 'medium' || !a.Priority);
        const lowPriority = filteredAccounts.filter(a => (a.Priority || '').toLowerCase() === 'low');

        // Dynamic target quota calculation from filtered targets
        const amountTargets = filteredTargets.filter(t => t.TargetType === 'Amount');
        const leadGenTargets = filteredTargets.filter(t => t.TargetType === 'Lead Generation');
        const leadApproachTargets = filteredTargets.filter(t => t.TargetType === 'Lead Approaching');

        // Revenue Target Quota (Amount)
        const companyTargetAmt = amountTargets.reduce((sum, t) => sum + (Number(t.TargetAmount) || 0), 0);
        const totalWonRev = wonTotal;
        const amtGoalPercent = companyTargetAmt > 0 ? Math.min(100, Math.round((totalWonRev / companyTargetAmt) * 100)) : (totalWonRev > 0 ? 100 : 0);

        // Lead Generation Target Quota
        const leadGenTarget = leadGenTargets.reduce((sum, t) => sum + (Number(t.TargetCount) || 0), 0);
        const leadGenAchieved = filteredAccounts.length;
        const leadGenPercent = leadGenTarget > 0 ? Math.min(100, Math.round((leadGenAchieved / leadGenTarget) * 100)) : (leadGenAchieved > 0 ? 100 : 0);

        // Lead Approaching Target Quota (Accounts with effective status 'Contacted' or 'Opportunity Created')
        const leadApproachTarget = leadApproachTargets.reduce((sum, t) => sum + (Number(t.TargetCount) || 0), 0);
        const approachedAccounts = filteredAccounts.filter(a => {
            const effStatus = getLeadEffectiveStatus(a);
            return effStatus === 'Contacted' || effStatus === 'Opportunity Created';
        });
        const leadApproachAchieved = approachedAccounts.length;
        const leadApproachPercent = leadApproachTarget > 0 ? Math.min(100, Math.round((leadApproachAchieved / leadApproachTarget) * 100)) : (leadApproachAchieved > 0 ? 100 : 0);

        // Visibility flags: Target cards should only be visible if a target quota is assigned (> 0)
        const hasRevenueTarget = amountTargets.length > 0 && companyTargetAmt > 0;
        const hasLeadGenTarget = leadGenTargets.length > 0 && leadGenTarget > 0;
        const hasLeadApproachTarget = leadApproachTargets.length > 0 && leadApproachTarget > 0;
        const hasAnyTarget = hasRevenueTarget || hasLeadGenTarget || hasLeadApproachTarget;

        return {
            totalPipeline, totalDeals: filteredOpportunities.length,
            openDeals: openDeals.length, openTotal,
            wonDeals: wonDeals.length, wonTotal,
            lostDeals: lostDeals.length, lostTotal,
            winRatio, lostRatio,
            totalAccounts: filteredAccounts.length,
            leadCount: leadAccounts.length, customerCount: customerAccounts.length, deadCount: deadAccounts.length,
            highPriorityCount: highPriority.length, mediumPriorityCount: mediumPriority.length, lowPriorityCount: lowPriority.length,
            companyTargetAmt, totalWonRev, amtGoalPercent,
            leadGenTarget, leadGenAchieved, leadGenPercent,
            leadApproachTarget, leadApproachAchieved, leadApproachPercent,
            hasRevenueTarget, hasLeadGenTarget, hasLeadApproachTarget, hasAnyTarget
        };
    }, [filteredOpportunities, filteredAccounts, filteredTargets, oppsByAccount]);

    // Helper to get month slots based on date filters selected at the top
    const monthSlots = useMemo(() => {
        if (startDate && endDate) {
            const slots = [];
            let curr = new Date(startDate + 'T00:00:00');
            const last = new Date(endDate + 'T00:00:00');
            while (curr <= last) {
                const y = curr.getFullYear();
                const m = String(curr.getMonth() + 1).padStart(2, '0');
                const key = `${y}-${m}`;
                const label = curr.toLocaleString('en-US', { month: 'short', year: '2-digit' });
                slots.push({ key, label });
                curr.setMonth(curr.getMonth() + 1);
            }
            return slots.length > 0 ? slots : [{ key: new Date().toISOString().slice(0, 7), label: 'Current' }];
        }

        // When "All Time" (no start/end date filter), list all months present in accounts or fallback
        const allMonthKeys = new Set();
        accounts.forEach(a => {
            if (a.CreatedAt) allMonthKeys.add(a.CreatedAt.slice(0, 7));
            if (a.ContactedAt) allMonthKeys.add(a.ContactedAt.slice(0, 7));
        });

        if (allMonthKeys.size > 0) {
            const sortedKeys = Array.from(allMonthKeys).sort();
            const minKey = sortedKeys[0];
            const maxKey = sortedKeys[sortedKeys.length - 1];
            const [minY, minM] = minKey.split('-').map(Number);
            const [maxY, maxM] = maxKey.split('-').map(Number);

            let curr = new Date(minY, minM - 1, 1);
            const last = new Date(maxY, maxM - 1, 1);
            const slots = [];
            while (curr <= last) {
                const y = curr.getFullYear();
                const m = String(curr.getMonth() + 1).padStart(2, '0');
                const key = `${y}-${m}`;
                const label = curr.toLocaleString('en-US', { month: 'short', year: '2-digit' });
                slots.push({ key, label });
                curr.setMonth(curr.getMonth() + 1);
            }
            if (slots.length > 0) return slots;
        }

        // Default: 6 months up to current month
        const slots = [];
        const now = new Date();
        for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const key = `${y}-${m}`;
            const label = d.toLocaleString('en-US', { month: 'short', year: '2-digit' });
            slots.push({ key, label });
        }
        return slots;
    }, [startDate, endDate, accounts]);

    // 1. Monthly Lead Generation Trend by Generator
    const leadGenMonthlyTrend = useMemo(() => {
        const userTotals = {};
        const monthData = monthSlots.map(m => {
            const monthAccounts = filteredAccounts.filter(a => {
                if (!a.CreatedAt) return false;
                return a.CreatedAt.slice(0, 7) === m.key;
            });

            const repCounts = {};
            monthAccounts.forEach(a => {
                const genId = Number(a.LeadGenerator || a.AssignedTo || 0);
                if (genId) {
                    repCounts[genId] = (repCounts[genId] || 0) + 1;
                    userTotals[genId] = (userTotals[genId] || 0) + 1;
                }
            });

            return {
                ...m,
                total: monthAccounts.length,
                repCounts
            };
        });

        const activeReps = users
            .filter(u => userTotals[Number(u.empId)] > 0 || users.length <= 5)
            .map((u, idx) => ({
                empId: Number(u.empId),
                name: (u.name || 'User').split(' ')[0],
                fullName: u.name || 'User',
                color: CHART_COLORS[idx % CHART_COLORS.length],
                total: userTotals[Number(u.empId)] || 0
            }));

        const maxTotal = Math.max(...monthData.map(m => m.total), 1);
        const totalAll = filteredAccounts.length;

        return { monthData, activeReps, maxTotal, totalAll };
    }, [monthSlots, filteredAccounts, users]);

    // 2. Monthly Lead Approaching / Outreach Trend by Responsible Rep
    const leadApproachMonthlyTrend = useMemo(() => {
        const approachedAccounts = filteredAccounts.filter(a => {
            const effStatus = getLeadEffectiveStatus(a);
            return effStatus === 'Contacted' || effStatus === 'Opportunity Created' || a.ContactedAt;
        });

        const userTotals = {};
        const monthData = monthSlots.map(m => {
            const monthAccounts = approachedAccounts.filter(a => {
                const dateStr = a.ContactedAt ? a.ContactedAt.slice(0, 7) : (a.UpdatedAt ? a.UpdatedAt.slice(0, 7) : a.CreatedAt.slice(0, 7));
                return dateStr === m.key;
            });

            const repCounts = {};
            monthAccounts.forEach(a => {
                const respId = Number(a.Responsible || a.AssignedTo || 0);
                if (respId) {
                    repCounts[respId] = (repCounts[respId] || 0) + 1;
                    userTotals[respId] = (userTotals[respId] || 0) + 1;
                }
            });

            return {
                ...m,
                total: monthAccounts.length,
                repCounts
            };
        });

        const activeReps = users
            .filter(u => userTotals[Number(u.empId)] > 0 || users.length <= 5)
            .map((u, idx) => ({
                empId: Number(u.empId),
                name: (u.name || 'User').split(' ')[0],
                fullName: u.name || 'User',
                color: CHART_COLORS[idx % CHART_COLORS.length],
                total: userTotals[Number(u.empId)] || 0
            }));

        const maxTotal = Math.max(...monthData.map(m => m.total), 1);
        const totalAll = approachedAccounts.length;

        return { monthData, activeReps, maxTotal, totalAll };
    }, [monthSlots, filteredAccounts, users, oppsByAccount]);



    const stageBreakdown = useMemo(() => {
        return STAGES.map(stg => {
            const matches = filteredOpportunities.filter(o => o.Stage === stg.key);
            const total = matches.reduce((sum, o) => sum + (Number(o.Amount) || 0), 0);
            return { ...stg, count: matches.length, total };
        });
    }, [filteredOpportunities]);

    const recentOpportunities = useMemo(() => {
        return [...filteredOpportunities]
            .sort((a, b) => new Date(b.CreatedAt || 0) - new Date(a.CreatedAt || 0))
            .slice(0, 5);
    }, [filteredOpportunities]);

    const recentActivities = useMemo(() => {
        const list = [];
        filteredOpportunities.slice(0, 3).forEach(opp => {
            list.push({
                id: `opp-${opp.OprID}`,
                title: opp.OpprName,
                desc: `Deal "${opp.OpprName}" updated (${opp.Stage})`,
                time: opp.CreatedAt ? new Date(opp.CreatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Recently'
            });
        });
        filteredAccounts.slice(0, 3).forEach(acc => {
            list.push({
                id: `acc-${acc.AccountID}`,
                title: acc.AccountName,
                desc: `${acc.AccountType || 'Account'} "${acc.AccountName}" active in ${acc.Status || 'Lead'}`,
                time: acc.CreatedAt ? new Date(acc.CreatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Recently'
            });
        });
        return list.slice(0, 4);
    }, [filteredOpportunities, filteredAccounts]);

    const handleQuickOppSubmit = async (e) => {
        e.preventDefault();
        if (!oppForm.OpprName.trim()) { alert("Opportunity Name is required"); return; }
        try {
            await api.post('/opportunities', oppForm);
            setShowQuickOppModal(false);
            setOppForm({ OpprName: '', Account: '', Amount: '', Stage: 'Qualification', Status: 'Open' });
            fetchAllDashboardData();
        } catch (err) { console.error("Quick Opp Create Failed:", err); alert("Failed to create opportunity"); }
    };

    const handleQuickAccSubmit = async (e) => {
        e.preventDefault();
        if (!accForm.AccountName.trim()) { alert("Account Name is required"); return; }
        try {
            await api.post('/leads', { ...accForm, AccountType: accModalType });
            setShowQuickAccModal(false);
            setAccForm({ AccountName: '', ContactPerson: '', ContactDesignation: '', CntPhone1: '', CntMail1: '', Linkedin: '', City: 'Lahore', Status: 'Lead', Priority: 'Medium', Notes: '' });
            fetchAllDashboardData();
        } catch (err) { console.error("Quick Account Create Failed:", err); alert("Failed to create account"); }
    };

    if (loading) {
        return (
            <Layout>
                <div className="dash-loading-state">
                    <div className="dash-spinner"></div>
                    <p>Loading SalesVault Dashboard...</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="dome-dashboard-wrap">

                {/* 1. TOP HEADER (DOME Reference Header) */}
                <div className="dome-dash-header-row">
                    <div className="dome-dash-title-wrap">
                        <h1 className="dome-dash-h1">Executive Overview</h1>
                        <span className="dome-dash-dots-menu" title="SalesVault Operations">···</span>
                    </div>
                    <div className="dome-dash-actions">
                        <button
                            type="button"
                            className="btn-dome-secondary"
                            onClick={handleManualRefresh}
                            disabled={refreshing}
                            title="Refresh Dashboard Data"
                        >
                            <FontAwesomeIcon icon={faSyncAlt} className={refreshing ? 'fa-spin' : ''} />
                            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                        </button>

                        <button
                            type="button"
                            className="btn-dome-secondary"
                            onClick={() => navigate('/targets')}
                            title="View Sales Targets & Quotas"
                        >
                            <FontAwesomeIcon icon={faBullseye} />
                            <span>Targets</span>
                        </button>

                        <div className="dome-new-menu-wrap">
                            <button
                                type="button"
                                className="btn-dome-new"
                                onClick={() => setShowNewDropdown(!showNewDropdown)}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <span>Create</span>
                            </button>
                            {showNewDropdown && (
                                <div className="dome-new-dropdown" onMouseLeave={() => setShowNewDropdown(false)}>
                                    <button
                                        type="button"
                                        className="dome-new-item"
                                        onClick={() => { setShowNewDropdown(false); setShowQuickOppModal(true); }}
                                    >
                                        <FontAwesomeIcon icon={faCoins} className="text-secondary" />
                                        <span>New Opportunity</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="dome-new-item"
                                        onClick={() => { setShowNewDropdown(false); setAccModalType('Organization'); setShowQuickAccModal(true); }}
                                    >
                                        <FontAwesomeIcon icon={faBuilding} className="text-secondary" />
                                        <span>New Org Account</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="dome-new-item"
                                        onClick={() => { setShowNewDropdown(false); setAccModalType('Individual'); setShowQuickAccModal(true); }}
                                    >
                                        <FontAwesomeIcon icon={faUser} className="text-secondary" />
                                        <span>New Individual Lead</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 2. PERIOD TOOLBAR STRIP */}
                <div className="dome-dash-subnav-strip">
                    <div className="dome-dash-filters-toolbar">
                        <div className="dome-period-pills">
                            <button
                                type="button"
                                className={`dome-period-btn ${!startDate && !endDate ? 'active' : ''}`}
                                onClick={() => setPresetDates('ALL')}
                            >
                                All Time
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${startDate.includes('-07-01') && endDate.includes('-12-31') ? 'active' : ''}`}
                                onClick={() => setPresetDates('CURRENT_H1')}
                            >
                                H1 (Jul&ndash;Dec)
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${startDate.includes('-01-01') && endDate.includes('-06-30') ? 'active' : ''}`}
                                onClick={() => setPresetDates('CURRENT_H2')}
                            >
                                H2 (Jan&ndash;Jun)
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${startDate.includes('-07-01') && endDate.includes('-06-30') ? 'active' : ''}`}
                                onClick={() => setPresetDates('FULL_FY')}
                            >
                                Full FY
                            </button>
                        </div>

                        <div className="dome-date-inputs-inline">
                            <input
                                className="dome-date-mini-input"
                                type="date"
                                value={startDate}
                                title="Start Date Filter"
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <span className="text-secondary text-11">&ndash;</span>
                            <input
                                className="dome-date-mini-input"
                                type="date"
                                value={endDate}
                                title="End Date Filter"
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            {(startDate || endDate) && (
                                <button
                                    type="button"
                                    className="dome-clear-date-btn"
                                    onClick={() => setPresetDates('ALL')}
                                    title="Reset Date Filters"
                                >
                                    <FontAwesomeIcon icon={faTimesCircle} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* 3. SECTION HEADING */}
                <h2 className="dome-section-h2">Overview</h2>

                {/* 4. TOP 4 KPI CARDS STRIP (Exact Reference Pattern) */}
                <div className="dome-kpi-strip">
                    {/* Card 1: Total Pipeline */}
                    <div className="dome-kpi-card" onClick={() => navigate('/opportunities')}>
                        <span className="dome-kpi-label">Total Pipeline</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">${metrics.totalPipeline.toLocaleString('en-US')}</span>
                            <span className="dome-kpi-pill">+{metrics.winRatio}% Win Rate</span>
                        </div>
                        <span className="dome-kpi-sub">{metrics.openDeals} deals in active pipeline</span>
                    </div>

                    {/* Card 2: Won Revenue */}
                    <div className="dome-kpi-card" onClick={() => navigate('/opportunities')}>
                        <span className="dome-kpi-label">Won Revenue</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">${metrics.wonTotal.toLocaleString('en-US')}</span>
                        </div>
                        <span className="dome-kpi-sub">
                            {metrics.hasRevenueTarget ? `${metrics.amtGoalPercent}% of $${metrics.companyTargetAmt.toLocaleString('en-US')} quota` : `${metrics.wonDeals} deals completed`}
                        </span>
                    </div>

                    {/* Card 3: Active Accounts & Leads */}
                    <div className="dome-kpi-card" onClick={() => navigate('/leads')}>
                        <span className="dome-kpi-label">Active Accounts &amp; Leads</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{metrics.totalAccounts}</span>
                        </div>
                        <span className="dome-kpi-sub">{metrics.highPriorityCount} high priority accounts</span>
                    </div>

                    {/* Card 4: Outreach Reach */}
                    <div className="dome-kpi-card" onClick={() => navigate('/outreach')}>
                        <span className="dome-kpi-label">Outreach Approached</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{metrics.leadApproachAchieved}</span>
                        </div>
                        <span className="dome-kpi-sub">
                            {metrics.hasLeadApproachTarget ? `${metrics.leadApproachPercent}% of ${metrics.leadApproachTarget} quota` : 'Contacted / in progress'}
                        </span>
                    </div>
                </div>

                {/* 5. MAIN 2-COLUMN GRID (Matching Reference) */}
                <div className="dome-dashboard-grid">

                    {/* ── LEFT COLUMN (Wide Column: ~68%) ── */}
                    <div className="dome-dash-left-col">

                        {/* Card A: Recent Documents / Opportunities (Exact Reference Match) */}
                        <div className="dome-dash-card">
                            <div className="dome-dash-card-header">
                                <h3 className="dome-dash-card-title">Recent Opportunities</h3>
                                <button
                                    type="button"
                                    className="dome-dash-card-link"
                                    onClick={() => navigate('/opportunities')}
                                >
                                    View all
                                </button>
                            </div>
                            <div className="dome-dash-table-wrap">
                                <table className="dome-dash-table">
                                    <thead>
                                        <tr>
                                            <th>Opportunity</th>
                                            <th>Account</th>
                                            <th>Stage</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentOpportunities.length > 0 ? (
                                            recentOpportunities.map((opp) => (
                                                <tr key={`opp-${opp.OprID}`}>
                                                    <td>
                                                        <span
                                                            className="dome-deal-link"
                                                            onClick={() => navigate('/opportunities')}
                                                        >
                                                            {opp.OpprName}
                                                        </span>
                                                    </td>
                                                    <td>{opp.AccountName || 'Direct'}</td>
                                                    <td>
                                                        <span className="badge-status badge-neutral">
                                                            {opp.Stage}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <strong>${Number(opp.Amount || 0).toLocaleString('en-US')}</strong>
                                                    </td>
                                                    <td>
                                                        <span className={`badge-status ${opp.Status === 'Win' || opp.Stage === 'Completed' ? 'badge-paid' : opp.Status === 'Lose' ? 'badge-rejected' : 'badge-submitted'}`}>
                                                            {opp.Status || 'Open'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="td-empty text-center">
                                                    No opportunities recorded in this period.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Card B: Target Quota Progress Meters (Compact & High-Density) */}
                        {metrics.hasAnyTarget && (
                            <div className="dome-dash-card">
                                <div className="dome-dash-card-header">
                                    <h3 className="dome-dash-card-title">Sales Target Quotas ({periodLabel})</h3>
                                    <button
                                        type="button"
                                        className="dome-dash-card-link"
                                        onClick={() => navigate('/targets')}
                                    >
                                        Manage Targets
                                    </button>
                                </div>
                                <div className="dome-target-meters-wrap">
                                    {metrics.hasRevenueTarget && (
                                        <div className="dome-target-meter-item" onClick={() => navigate('/opportunities')}>
                                            <div className="dome-target-meter-top">
                                                <span className="dome-target-meter-label">Revenue Quota (Amount)</span>
                                                <span className="dome-target-meter-pct">{metrics.amtGoalPercent}%</span>
                                            </div>
                                            <div className="dome-target-bar-bg">
                                                <div className="dome-target-bar-fill" style={{ width: `${metrics.amtGoalPercent}%` }}></div>
                                            </div>
                                            <div className="dome-target-meter-values">
                                                <span>Won: ${metrics.totalWonRev.toLocaleString('en-US')}</span>
                                                <span>Target: ${metrics.companyTargetAmt.toLocaleString('en-US')}</span>
                                            </div>
                                        </div>
                                    )}

                                    {metrics.hasLeadGenTarget && (
                                        <div className="dome-target-meter-item" onClick={() => navigate('/leads')}>
                                            <div className="dome-target-meter-top">
                                                <span className="dome-target-meter-label">Lead Generation Quota</span>
                                                <span className="dome-target-meter-pct">{metrics.leadGenPercent}%</span>
                                            </div>
                                            <div className="dome-target-bar-bg">
                                                <div className="dome-target-bar-fill" style={{ width: `${metrics.leadGenPercent}%` }}></div>
                                            </div>
                                            <div className="dome-target-meter-values">
                                                <span>Generated: {metrics.leadGenAchieved} Leads</span>
                                                <span>Target: {metrics.leadGenTarget} Leads</span>
                                            </div>
                                        </div>
                                    )}

                                    {metrics.hasLeadApproachTarget && (
                                        <div className="dome-target-meter-item" onClick={() => navigate('/outreach')}>
                                            <div className="dome-target-meter-top">
                                                <span className="dome-target-meter-label">Outreach / Approaching Quota</span>
                                                <span className="dome-target-meter-pct">{metrics.leadApproachPercent}%</span>
                                            </div>
                                            <div className="dome-target-bar-bg">
                                                <div className="dome-target-bar-fill" style={{ width: `${metrics.leadApproachPercent}%` }}></div>
                                            </div>
                                            <div className="dome-target-meter-values">
                                                <span>Approached: {metrics.leadApproachAchieved}</span>
                                                <span>Target: {metrics.leadApproachTarget}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Card C: Pipeline Stage Funnel Strip (Compact Horizontal) */}
                        <div className="dome-dash-card">
                            <div className="dome-dash-card-header">
                                <h3 className="dome-dash-card-title">Pipeline Stage Distribution</h3>
                                <button
                                    type="button"
                                    className="dome-dash-card-link"
                                    onClick={() => navigate('/opportunities')}
                                >
                                    Full Pipeline
                                </button>
                            </div>
                            <div className="dome-funnel-compact-grid">
                                {stageBreakdown.map((stg) => {
                                    return (
                                        <div
                                            key={stg.key}
                                            className="dome-funnel-cell"
                                            onClick={() => navigate('/opportunities')}
                                            title={`${stg.label}: ${stg.count} deals ($${stg.total.toLocaleString('en-US')})`}
                                        >
                                            <div className="dome-funnel-cell-name">{stg.label}</div>
                                            <div className="dome-funnel-cell-count">{stg.count}</div>
                                            <div className="dome-funnel-cell-amount">{stg.total > 0 ? (stg.total >= 1000 ? `${Math.round(stg.total / 1000)}k` : stg.total) : '0'} USD</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Card D: Activity Trends (Interactive SVG Curves) */}
                        <div className="dash-row-2col mb-0">
                            <div className="dome-dash-card dome-trend-card">
                                <div className="dome-dash-card-header">
                                    <h3 className="dome-dash-card-title">Lead Generation Trend</h3>
                                    <span className="section-badge section-badge-blue">
                                        {leadGenMonthlyTrend.totalAll} Total
                                    </span>
                                </div>
                                <div className="trend-chart-card-body">
                                    <AreaCurveTrendChart
                                        trendData={leadGenMonthlyTrend}
                                        chartId="gen"
                                        metricLabel="Leads Generated"
                                        metricVerb="leads generated"
                                        emptyMessage="No lead generation in this period"
                                        emptyIcon={faUsers}
                                    />
                                </div>
                            </div>

                            <div className="dome-dash-card dome-trend-card">
                                <div className="dome-dash-card-header">
                                    <h3 className="dome-dash-card-title">Outreach Approach Trend</h3>
                                    <span className="section-badge section-badge-purple">
                                        {leadApproachMonthlyTrend.totalAll} Total
                                    </span>
                                </div>
                                <div className="trend-chart-card-body">
                                    <AreaCurveTrendChart
                                        trendData={leadApproachMonthlyTrend}
                                        chartId="approach"
                                        metricLabel="Leads Approached"
                                        metricVerb="leads approached"
                                        emptyMessage="No outreach recorded in this period"
                                        emptyIcon={faBuilding}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* ── RIGHT COLUMN (Compact Column: ~32%) ── */}
                    <div className="dome-dash-right-col">

                        {/* Card 1: Quick Access 2x2 Grid (Exact Reference Match) */}
                        <div className="dome-dash-card">
                            <div className="dome-dash-card-header">
                                <h3 className="dome-dash-card-title">Quick Access</h3>
                            </div>
                            <div className="dome-quick-access-grid">
                                <button
                                    type="button"
                                    className="dome-qa-btn"
                                    onClick={() => setShowQuickOppModal(true)}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="dome-qa-icon" />
                                    <span className="dome-qa-title">New Deal</span>
                                    <span className="dome-qa-desc">Add deal to pipeline</span>
                                </button>
                                <button
                                    type="button"
                                    className="dome-qa-btn"
                                    onClick={() => navigate('/leads')}
                                >
                                    <FontAwesomeIcon icon={faBuilding} className="dome-qa-icon" />
                                    <span className="dome-qa-title">Accounts</span>
                                    <span className="dome-qa-desc">Manage client directory</span>
                                </button>
                                <button
                                    type="button"
                                    className="dome-qa-btn"
                                    onClick={() => navigate('/contacts')}
                                >
                                    <FontAwesomeIcon icon={faAddressBook} className="dome-qa-icon" />
                                    <span className="dome-qa-title">Contacts</span>
                                    <span className="dome-qa-desc">Key decision makers</span>
                                </button>
                                <button
                                    type="button"
                                    className="dome-qa-btn"
                                    onClick={() => navigate('/targets')}
                                >
                                    <FontAwesomeIcon icon={faBullseye} className="dome-qa-icon" />
                                    <span className="dome-qa-title">Quotas</span>
                                    <span className="dome-qa-desc">Target allocations</span>
                                </button>
                            </div>
                        </div>

                        {/* Card 2: Recent Activity Feed (Exact Reference Match) */}
                        <div className="dome-dash-card">
                            <div className="dome-dash-card-header">
                                <h3 className="dome-dash-card-title">Recent Activity</h3>
                                <button
                                    type="button"
                                    className="dome-dash-card-link"
                                    onClick={() => navigate('/leads')}
                                >
                                    View all
                                </button>
                            </div>
                            <div className="dome-activity-list">
                                {recentActivities.length > 0 ? (
                                    recentActivities.map(act => (
                                        <div key={act.id} className="dome-activity-row">
                                            <span className="dome-activity-dot"></span>
                                            <div className="dome-activity-content">
                                                <div>{act.desc}</div>
                                                <span className="dome-activity-time">{act.time}</span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="td-empty text-center">No recent activity</div>
                                )}
                            </div>
                        </div>

                        {/* Card 3: Priority & Status Matrix (Compact) */}
                        <div className="dome-dash-card">
                            <div className="dome-dash-card-header">
                                <h3 className="dome-dash-card-title">Priority &amp; Status Matrix</h3>
                                <button
                                    type="button"
                                    className="dome-dash-card-link"
                                    onClick={() => navigate('/leads')}
                                >
                                    View all
                                </button>
                            </div>
                            <div className="dome-matrix-compact-grid">
                                <div className="dome-matrix-box" onClick={() => navigate('/leads')}>
                                    <div className="dome-matrix-box-num">{metrics.highPriorityCount}</div>
                                    <div className="dome-matrix-box-label">High Priority</div>
                                </div>
                                <div className="dome-matrix-box" onClick={() => navigate('/leads')}>
                                    <div className="dome-matrix-box-num">{metrics.mediumPriorityCount}</div>
                                    <div className="dome-matrix-box-label">Med Priority</div>
                                </div>
                                <div className="dome-matrix-box" onClick={() => navigate('/leads')}>
                                    <div className="dome-matrix-box-num">{metrics.lowPriorityCount}</div>
                                    <div className="dome-matrix-box-label">Low Priority</div>
                                </div>
                                <div className="dome-matrix-box" onClick={() => navigate('/leads')}>
                                    <div className="dome-matrix-box-num">{metrics.leadCount}</div>
                                    <div className="dome-matrix-box-label">Leads</div>
                                </div>
                                <div className="dome-matrix-box" onClick={() => navigate('/leads')}>
                                    <div className="dome-matrix-box-num">{metrics.customerCount}</div>
                                    <div className="dome-matrix-box-label">Customers</div>
                                </div>
                                <div className="dome-matrix-box" onClick={() => navigate('/leads')}>
                                    <div className="dome-matrix-box-num">{metrics.deadCount}</div>
                                    <div className="dome-matrix-box-label">Closed/Dead</div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* QUICK OPPORTUNITY MODAL */}
                {showQuickOppModal && (
                    <div className="modal-overlay" onClick={() => setShowQuickOppModal(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header modal-header-flex">
                                <h3>Quick Create Opportunity</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowQuickOppModal(false)}
                                    className="modal-btn-close"
                                    title="Close Modal"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                            <form onSubmit={handleQuickOppSubmit} className="form-modal-container">
                                <div className="modal-body">
                                    <div className="form-grid-3col">
                                        <div className="input-group input-group-span-2">
                                            <label>Opportunity Name <span className="text-danger">*</span></label>
                                            <input type="text" placeholder="e.g. BioPicta Laboratory Expansion" value={oppForm.OpprName} onChange={(e) => setOppForm({ ...oppForm, OpprName: e.target.value })} required />
                                        </div>
                                        <div className="input-group">
                                            <label>Amount (USD $)</label>
                                            <input type="number" placeholder="e.g. 75000" value={oppForm.Amount} onChange={(e) => setOppForm({ ...oppForm, Amount: e.target.value })} />
                                        </div>
                                        <div className="input-group input-group-span-2">
                                            <label>Account / Client</label>
                                            <select value={oppForm.Account} onChange={(e) => setOppForm({ ...oppForm, Account: e.target.value })}>
                                                <option value="">Select Account...</option>
                                                {accounts.map(acc => (<option key={acc.AccountID} value={acc.AccountID}>{acc.AccountName}</option>))}
                                            </select>
                                        </div>
                                        <div className="input-group">
                                            <label>Pipeline Stage</label>
                                            <select value={oppForm.Stage} onChange={(e) => setOppForm({ ...oppForm, Stage: e.target.value })}>
                                                {STAGES.map(stg => (<option key={stg.key} value={stg.key}>{stg.label}</option>))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn" onClick={() => setShowQuickOppModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-brand">Create Deal</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* QUICK ACCOUNT MODAL */}
                {showQuickAccModal && (
                    <div className="modal-overlay" onClick={() => setShowQuickAccModal(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header modal-header-flex">
                                <h3>Quick Create {accModalType === 'Individual' ? 'Individual Lead' : 'Organization Account'}</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowQuickAccModal(false)}
                                    className="modal-btn-close"
                                    title="Close Modal"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                            <form onSubmit={handleQuickAccSubmit} className="form-modal-container">
                                <div className="modal-body">
                                    <div className="form-grid-3col">
                                        <div className="input-group input-group-span-2">
                                            <label>{accModalType === 'Individual' ? 'Full Name' : 'Company / Organization Name'} <span className="text-danger">*</span></label>
                                            <input type="text" placeholder={accModalType === 'Individual' ? 'e.g. Dr. Asad Malik' : 'e.g. Apex Biotech Labs'} value={accForm.AccountName} onChange={(e) => setAccForm({ ...accForm, AccountName: e.target.value })} required />
                                        </div>
                                        <div className="input-group">
                                            <label>Account Status</label>
                                            <select value={accForm.Status} onChange={(e) => setAccForm({ ...accForm, Status: e.target.value })}>
                                                <option value="Lead">Lead</option>
                                                <option value="Customer">Customer</option>
                                                <option value="Dead">Dead</option>
                                            </select>
                                        </div>
                                        {accModalType === 'Organization' && (
                                            <div className="input-group input-group-span-2">
                                                <label>Contact Person</label>
                                                <input type="text" placeholder="e.g. Dr. Sarah Chen" value={accForm.ContactPerson} onChange={(e) => setAccForm({ ...accForm, ContactPerson: e.target.value })} />
                                            </div>
                                        )}
                                        <div className="input-group">
                                            <label>Primary Phone</label>
                                            <input type="text" placeholder="e.g. +1 555 123 4567" value={accForm.CntPhone1} onChange={(e) => setAccForm({ ...accForm, CntPhone1: e.target.value })} />
                                        </div>
                                        <div className="input-group">
                                            <label>Primary Email</label>
                                            <input type="email" placeholder="e.g. info@organization.com" value={accForm.CntMail1} onChange={(e) => setAccForm({ ...accForm, CntMail1: e.target.value })} />
                                        </div>
                                        <div className="input-group">
                                            <label>LinkedIn URL</label>
                                            <input type="text" placeholder="e.g. https://www.linkedin.com/in/..." value={accForm.Linkedin} onChange={(e) => setAccForm({ ...accForm, Linkedin: e.target.value })} />
                                        </div>
                                        <div className="input-group">
                                            <label>Priority</label>
                                            <select value={accForm.Priority} onChange={(e) => setAccForm({ ...accForm, Priority: e.target.value })}>
                                                <option value="High">High Priority</option>
                                                <option value="Medium">Medium Priority</option>
                                                <option value="Low">Low Priority</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn" onClick={() => setShowQuickAccModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-brand">Create Account</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

            </div>
        </Layout>
    );
}

export default Dashboard;
